<script setup lang="ts">
import { WEEKDAY_LABELS } from '../constants'
import type { WeekDateView } from '../types'
import imgNavChevron from '@/assets/矩形@2x.png'

const props = defineProps<{
  inspectorTitle: string
  calendarMonthLabel: string
  weekDays: WeekDateView[]
  embedded: boolean
}>()

const emit = defineEmits<{
  'shift-week': [delta: number]
  'shift-month': [delta: number]
  'select-date': [date: Date]
}>()

function onToolbarNav(delta: number) {
  if (props.embedded) emit('shift-month', delta)
  else emit('shift-week', delta)
}
</script>

<template>
  <h2
    class="calendar-inspector-title"
    :class="{ 'schedule-ctx--embedded': embedded }"
    :title="inspectorTitle"
  >
    {{ inspectorTitle }}
  </h2>

  <div class="week-toolbar" :class="{ 'schedule-ctx--embedded': embedded }">
    <button
      type="button"
      class="week-nav"
      :aria-label="embedded ? 'Previous month' : 'Previous week'"
      @click="onToolbarNav(-1)"
    >
      <img :src="imgNavChevron" class="week-nav-ic" alt="" />
    </button>
    <span class="week-month">{{ calendarMonthLabel }}</span>
    <button
      type="button"
      class="week-nav week-nav--next"
      :aria-label="embedded ? 'Next month' : 'Next week'"
      @click="onToolbarNav(1)"
    >
      <img :src="imgNavChevron" class="week-nav-ic" alt="" />
    </button>
  </div>

  <div class="calendar-week-block" :class="{ 'schedule-ctx--embedded': embedded }">
    <div class="week-date-strip" :class="{ 'schedule-ctx--embedded': embedded }">
      <button type="button" class="week-strip-nav" aria-label="Previous week" @click="emit('shift-week', -1)">
        <img :src="imgNavChevron" class="week-strip-nav-ic" alt="" />
      </button>
      <div class="week-grid">
        <div v-for="day in weekDays" :key="day.key" class="week-day-col">
          <span class="week-dow">{{ WEEKDAY_LABELS[day.date.getDay()] }}</span>
          <button
            type="button"
            class="week-date-card"
            :class="day.cardClass"
            :disabled="!day.selectable"
            @click="emit('select-date', day.date)"
          >
            <span class="week-date-card-bar" :class="day.barClass" aria-hidden="true" />
            <span class="week-date-card-num">{{ day.date.getDate() }}</span>
          </button>
        </div>
      </div>
      <button
        type="button"
        class="week-strip-nav week-strip-nav--next"
        aria-label="Next week"
        @click="emit('shift-week', 1)"
      >
        <img :src="imgNavChevron" class="week-strip-nav-ic" alt="" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.schedule-ctx--embedded.calendar-inspector-title {
  position: absolute;
  top: 16px;
  left: 17px;
  right: 17px;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0;
  color: #000000;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.week-toolbar.schedule-ctx--embedded {
  position: absolute;
  top: var(--cal-month-top);
  left: 207px;
  right: 204px;
  z-index: 1;
  box-sizing: border-box;
  width: auto;
  max-width: none;
  height: var(--cal-month-row-h);
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.schedule-ctx--embedded.calendar-week-block {
  position: static;
  display: block;
  flex: none;
  width: 100%;
  height: 0;
  min-height: 0;
  max-height: none;
  margin: 0;
  padding: 0;
  overflow: visible;
}

.week-date-strip.schedule-ctx--embedded {
  position: absolute;
  top: var(--cal-week-strip-top);
  left: 17px;
  right: 15px;
  bottom: auto;
  margin: 0;
  margin-bottom: 0;
  padding: 0;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 0;
  box-sizing: border-box;
}

.week-date-strip.schedule-ctx--embedded .week-grid {
  flex: 0 0 auto;
  width: calc(7 * 57px + 6 * 16px);
  min-width: calc(7 * 57px + 6 * 16px);
  max-width: calc(7 * 57px + 6 * 16px);
  margin-left: 31px;
  margin-right: 0;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
}

.week-date-strip.schedule-ctx--embedded .week-day-col {
  flex: 0 0 57px;
  width: 57px;
  min-width: 57px;
  max-width: 57px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin: 0;
  box-sizing: border-box;
}

.week-date-strip.schedule-ctx--embedded .week-dow {
  height: 24px;
  margin: 0;
  padding: 0;
  line-height: 24px;
  text-align: center;
}

.week-date-strip.schedule-ctx--embedded .week-strip-nav {
  position: absolute;
  top: 28px;
  z-index: 2;
  flex-shrink: 0;
  width: 24px;
  min-width: 24px;
  max-width: 24px;
  height: 40px;
  min-height: 40px;
  max-height: 40px;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.week-date-strip.schedule-ctx--embedded > .week-strip-nav:first-of-type {
  left: 0;
}

.week-date-strip.schedule-ctx--embedded .week-strip-nav--next {
  position: static;
  top: auto;
  right: auto;
  left: auto;
  flex-shrink: 0;
  margin-left: 8px;
}

.week-toolbar.schedule-ctx--embedded .week-month {
  flex: 0 1 auto;
  min-width: 0;
  margin: 0;
  padding: 0;
  white-space: nowrap;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0;
  color: #000000;
  text-align: center;
}

.week-toolbar.schedule-ctx--embedded .week-nav {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.week-toolbar.schedule-ctx--embedded .week-nav-ic {
  width: 16px;
  height: 16px;
  display: block;
  object-fit: contain;
}

.week-toolbar.schedule-ctx--embedded .week-nav--next .week-nav-ic {
  transform: scaleX(-1);
}

.calendar-inspector-title {
  flex-shrink: 0;
  margin: 0 0 20px;
  padding: 0;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0;
  color: #000000;
  text-align: left;
  max-width: 100%;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.week-toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 12px;
}

.week-nav {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.week-nav-ic {
  width: 16px;
  height: 16px;
  display: block;
  object-fit: contain;
}

.week-nav--next .week-nav-ic {
  transform: scaleX(-1);
}

.week-month {
  white-space: nowrap;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0;
  color: #000000;
  text-align: center;
  min-width: 0;
}

.week-date-strip {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
  margin-bottom: 30px;
}

.week-strip-nav {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 40px;
  padding: 0;
  margin-bottom: 0;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #f7f7f7;
  cursor: pointer;
  box-sizing: border-box;
}

.week-strip-nav-ic {
  width: 16px;
  height: 16px;
  display: block;
  object-fit: contain;
  flex-shrink: 0;
}

.week-strip-nav--next .week-strip-nav-ic {
  transform: scaleX(-1);
}

.calendar-week-block {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.week-grid {
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 16px;
}

.week-day-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.week-dow {
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0;
  color: #000000;
  text-align: center;
}

.week-date-card-bar {
  display: block;
  width: 57px;
  max-width: 57px;
  height: 8px;
  flex-shrink: 0;
  border-radius: 8px 8px 0 0;
}

.bar-gray {
  background: #cccccc;
}

.bar-red {
  background: #e54848;
}

.bar-blue {
  background: #00aeef;
}

.bar-green {
  background: #98d5be;
}

.week-date-card {
  width: 57px;
  max-width: 57px;
  height: 40px;
  min-height: 40px;
  max-height: 40px;
  padding: 0;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;
}

.week-date-card--past,
.week-date-card--past:disabled {
  cursor: not-allowed;
  pointer-events: none;
  background: #ffffff;
  border-color: #e0e0e0;
}

.week-date-card--past .week-date-card-num,
.week-date-card--past:disabled .week-date-card-num {
  /* color: #999999; */
}

.week-date-card--past .week-date-card-bar,
.week-date-card--past:disabled .week-date-card-bar {
  opacity: 1;
}

.week-date-card--disabled,
.week-date-card:disabled {
  cursor: not-allowed;
  /* background: #9e9e9e; */
  /* border-color: #9e9e9e; */
  pointer-events: none;
}

.week-date-card--disabled .week-date-card-num,
.week-date-card:disabled .week-date-card-num {
  /* color: #ffffff; */
}

.week-date-card--disabled .week-date-card-bar,
.week-date-card:disabled .week-date-card-bar {
  opacity: 0.35;
}

.week-nav:disabled,
.week-strip-nav:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.week-date-card-num {
  display: block;
  flex: 1;
  align-self: center;
  width: fit-content;
  min-width: 10px;
  height: 24px;
  min-height: 24px;
  max-height: 24px;
  margin: auto 0;
  padding: 0;
  box-sizing: border-box;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-weight: 500;
  font-size: 16px;
  font-style: normal;
  line-height: 24px;
  letter-spacing: 0;
  color: #000000;
  text-align: left;
  white-space: nowrap;
}

.week-date-card--selected-red {
  border-color: #e0e0e0;
  background: #fdeaea;
}

.week-date-card--selected-blue {
  border-color: #e0e0e0;
  background: #e6f7fd;
}

.week-date-card--selected-green {
  border-color: #e0e0e0;
  background: #eef8f3;
}
</style>
